export interface WizardDraftSeed {
  name?: string;
  sku?: string;
  reference?: string;
  description?: string;
  sellingPrice?: string | number | null;
  mediaCount?: number;
}

/** Opening /products/new with empty fields must not create a draft. Images count as meaningful work. */
export function wizardDraftIsMeaningful(seed: WizardDraftSeed): boolean {
  if (Number(seed.mediaCount ?? 0) > 0) return true;
  const name = String(seed.name ?? "").trim();
  const sku = String(seed.sku ?? "").trim();
  const reference = String(seed.reference ?? "").trim();
  const description = String(seed.description ?? "").trim();
  if (name.length >= 2) return true;
  if (sku.length >= 2) return true;
  if (reference.length >= 2) return true;
  if (description.length >= 12) return true;
  const price = seed.sellingPrice;
  if (typeof price === "number" && Number.isFinite(price) && price > 0) return true;
  if (typeof price === "string" && price.trim() !== "" && Number(price) > 0) return true;
  return false;
}

export function wizardDraftTitle(seed: WizardDraftSeed): string {
  const name = String(seed.name ?? "").trim();
  if (name) return name;
  const sku = String(seed.sku ?? seed.reference ?? "").trim();
  if (sku) return `Draft ${sku}`;
  return "Incomplete product";
}

export function nextWizardStepFromPayload(payload: { step?: string; productType?: string; hasVariants?: boolean }): string {
  const step = payload.step ?? "";
  if (["product", "variants", "selling", "review"].includes(step)) return step;
  if (["type", "details", "extras"].includes(step)) return "product";
  if (step === "stock") return "variants";
  return "product";
}

export type WizardDraftSaveStatus = "idle" | "saving" | "saved" | "error";

export interface WizardVariationOptionPayload {
  name: string;
  position: number;
  values: { value: string; position: number }[];
}

export interface WizardVariationVariantPayload {
  sku: string;
  barcode: string | null;
  option_values: Record<string, string>;
  cost_price: number | null;
  selling_price: number | null;
  weight_kg: number | null;
  length_cm: number | null;
  width_cm: number | null;
  height_cm: number | null;
  opening_qty: number | null;
  position: number;
  status: string;
  /**
   * Inclusion state for this combination. Absent on legacy drafts, where every
   * submitted variant was included by definition.
   */
  included?: boolean;
}

/**
 * One editable matrix row, including excluded combinations.
 *
 * `variants` above is the submission projection (included rows only). Without a
 * full matrix, resuming a draft silently dropped every excluded row together
 * with its SKU lock and entered values. This is the backward-compatible
 * addition: legacy drafts have no `combinations` field and restore from
 * `variants` exactly as before.
 */
export interface WizardCombinationPayload {
  values: string[];
  sku: string;
  barcode: string | null;
  included: boolean;
  price: number | null;
  cost: number | null;
  weight_kg: number | null;
  length_cm: number | null;
  width_cm: number | null;
  height_cm: number | null;
  qty: number | null;
  status: string;
}

export interface WizardVariationPayload {
  options: WizardVariationOptionPayload[];
  variants: WizardVariationVariantPayload[];
  /** Full editable matrix, excluded rows included. Preferred over `variants` on restore. */
  combinations?: WizardCombinationPayload[];
  combinationCount: number;
  duplicateSkus: string[];
  hasData: boolean;
  /** SKUs the operator typed by hand. Restored so those SKUs stay locked after resume. */
  manualSkus?: string[];
  /** SKU pattern the operator was using. Restored so pattern and manual locks cannot diverge. */
  skuPattern?: string;
}

function combinationFromPayload(
  payload: WizardCombinationPayload,
  options: import("./products").ProductOption[],
): import("./products").CombinationRow {
  const values = options.map((_, index) => payload.values?.[index] ?? "");
  const optionAssignments: Record<string, string> = {};
  options.forEach((option, index) => {
    const match = option.values.find((value) => value.value === payload.values?.[index]);
    if (match) optionAssignments[option.name] = match.id;
  });
  return {
    values,
    optionAssignments,
    sku: payload.sku,
    barcode: payload.barcode ?? null,
    included: payload.included !== false,
    price: payload.price ?? null,
    cost: payload.cost ?? null,
    weightKg: payload.weight_kg ?? null,
    lengthCm: payload.length_cm ?? null,
    widthCm: payload.width_cm ?? null,
    heightCm: payload.height_cm ?? null,
    qty: payload.qty ?? null,
    status: (payload.status === "draft" || payload.status === "paused" || payload.status === "archived"
      ? payload.status
      : "active") as import("./products").VariantStatus,
  };
}

/** Legacy drafts stored only the included submission projection. Restore it as included rows. */
function combinationFromVariant(
  variant: WizardVariationVariantPayload,
  options: import("./products").ProductOption[],
): import("./products").CombinationRow {
  const values = options.map((option) => variant.option_values?.[option.name] ?? "");
  const optionAssignments: Record<string, string> = {};
  for (const option of options) {
    const match = option.values.find((value) => value.value === variant.option_values?.[option.name]);
    if (match) optionAssignments[option.name] = match.id;
  }
  return {
    values,
    optionAssignments,
    sku: variant.sku,
    barcode: variant.barcode,
    included: variant.included !== false,
    price: variant.selling_price,
    cost: variant.cost_price,
    weightKg: variant.weight_kg,
    lengthCm: variant.length_cm,
    widthCm: variant.width_cm,
    heightCm: variant.height_cm,
    qty: variant.opening_qty,
    status: (variant.status === "draft" || variant.status === "paused" || variant.status === "archived"
      ? variant.status
      : "active") as import("./products").VariantStatus,
  };
}

/** Restore option + combination editor state from a server draft. Replay-safe: same payload → same SKUs, no extras. */
export function restoreVariationEditorFromPayload(payload: WizardVariationPayload | null | undefined): {
  options: import("./products").ProductOption[];
  combinations: import("./products").CombinationRow[];
} | null {
  if (!payload?.options?.length) return null;
  const options = payload.options
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((option, optionIndex) => {
      const optionId = `opt-${optionIndex}`;
      return {
        id: optionId,
        org_id: "",
        product_id: "",
        name: option.name,
        position: optionIndex,
        status: "active" as const,
        values: (option.values ?? [])
          .slice()
          .sort((a, b) => a.position - b.position)
          .map((value, valueIndex) => ({
            id: `tmp-${optionIndex}-${valueIndex}`,
            org_id: "",
            product_option_id: optionId,
            value: value.value,
            position: valueIndex,
            status: "active" as const,
          })),
      };
    });

  const combinations = (payload.combinations && payload.combinations.length > 0)
    ? payload.combinations.map((row) => combinationFromPayload(row, options))
    : (payload.variants ?? []).map((variant) => combinationFromVariant(variant, options));

  return { options, combinations };
}

export function shouldAutosaveWizardDraft(opts: {
  created: boolean;
  hydrationReady: boolean;
  meaningful: boolean;
}): boolean {
  return !opts.created && opts.hydrationReady && opts.meaningful;
}

export function applyDraftSaveOutcome(opts: {
  requestSeq: number;
  latestSeq: number;
  ok: boolean;
}): WizardDraftSaveStatus | null {
  if (opts.requestSeq !== opts.latestSeq) return null;
  return opts.ok ? "saved" : "error";
}
